import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  HTMLElementTypeEnum,
  IconSizeEnum,
  TIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Debounce utility
const useDebounce = (callback: () => void, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(callback, delay);
  }, [callback, delay]);
};

type TMentionShowSelectedFilterProps = {
  items: string[];
  classIcon?: string;
  isDarkTheme?: boolean;
  icon: TIcon;
};

export const MentionShowSelectedFilter = ({
  items,
  classIcon,
  isDarkTheme,
  icon: Icon,
}: TMentionShowSelectedFilterProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<string[]>([]);
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);

  const calculateVisibleItems = useCallback(() => {
    if (!containerRef.current || items.length === 0) return;

    const containerWidth = containerRef.current.offsetWidth ?? 150;
    const children = Array.from(containerRef.current.children);

    if (children.length === 0) {
      setVisibleItems(items.slice(0, 1));
      setHiddenItems(items.slice(1));
      return;
    }

    let totalWidth = 0;
    let lastIndex = 0;
    const badgeWidthEstimate = 30;
    const maxContentWidth = containerWidth - badgeWidthEstimate;

    for (let i = 0; i < children.length && i < items.length; i++) {
      const child = children[i] as HTMLElement;
      const childWidth = child.offsetWidth || 0;

      if (totalWidth + childWidth <= maxContentWidth) {
        totalWidth += childWidth;
        lastIndex = i;
      } else {
        break;
      }
    }

    const finalIndex = Math.max(0, lastIndex);
    const newVisibleItems = items.slice(0, finalIndex + 1);
    const newHiddenItems = items.slice(finalIndex + 1);

    setVisibleItems(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(newVisibleItems)) {
        return newVisibleItems;
      }
      return prev;
    });

    setHiddenItems(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(newHiddenItems)) {
        return newHiddenItems;
      }
      return prev;
    });
  }, [items]);

  const debouncedCalculate = useDebounce(calculateVisibleItems, 100);

  useEffect(() => {
    const timer = setTimeout(calculateVisibleItems, 0);
    return () => clearTimeout(timer);
  }, [calculateVisibleItems]);

  useEffect(() => {
    const handleResize = () => {
      debouncedCalculate();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [debouncedCalculate]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-size-4">
      <Icon className={classIcon} size={IconSizeEnum.MD} />
      <Typography
        color={isDarkTheme ? FontColorEnum.Neutral : FontColorEnum.Dark10}
        fontSize={FontSizeEnum.Basic}
        weight={FontWeightEnum.Semibold}
        element={HTMLElementTypeEnum.Span}
      >
        {items.length}:
      </Typography>
      <div ref={containerRef} className="flex max-w-[150px] overflow-hidden text-ellipsis">
        {visibleItems.map((item, index) => (
          <React.Fragment key={index}>
            {index < visibleItems.length - 1 ? (
              <Typography
                color={isDarkTheme ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                element={HTMLElementTypeEnum.Span}
                className="whitespace-nowrap"
              >
                {item}, &nbsp;
              </Typography>
            ) : (
              <Typography
                color={isDarkTheme ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                element={HTMLElementTypeEnum.Span}
                className="whitespace-nowrap"
              >
                {item}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </div>
      {hiddenItems.length > 0 && (
        <Tooltip
          help={hiddenItems.filter(Boolean).join(', ')}
          className="!z-[9999] flex !w-[18px] !flex-col"
          grow
        >
          <div>
            <Badge
              variant={
                isDarkTheme ? BadgeVariantEnum.NeutralOutline : BadgeVariantEnum.DefaultOutline
              }
              className="py-3 !text-basic"
            >
              +{hiddenItems.length}
            </Badge>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
